'use client';

import { useState } from 'react';
import { Box } from '@mui/material';

import {
  GamesView,
  LiveView,
  PreviousMarketsToggle,
  PreviousMarketsView,
  SportPropsView,
  SportsCommentsSection,
} from '@/components';
import { EventResponse } from '@/types/market.types';

interface SportContentProps {
  activeTab: 'games' | 'props';
  sportSlug: string;
  games: EventResponse[];
  propsData: EventResponse[];
  isCurrentSportPropsLoading: boolean;
  isPropsLoading: boolean;
  gamesByDate: Record<string, EventResponse[]>;
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick: (marketId: string, outcomeId: string, isBuy: boolean) => void;
  columnVisibility: { hasMoneyline: boolean; hasSpread: boolean; hasTotal: boolean };
  seriesId: number | null;
  showComments?: boolean;
  activeOutcomeId?: string;
  tagId?: number;
  searchQuery?: string;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export const SportContent = ({
  activeTab,
  sportSlug,
  games,
  propsData,
  isCurrentSportPropsLoading,
  isPropsLoading,
  gamesByDate,
  onTeamClick,
  onPropTradeClick,
  columnVisibility,
  seriesId,
  showComments,
  activeOutcomeId,
  searchQuery,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}: SportContentProps) => {
  const [showPreviousMarkets, setShowPreviousMarkets] = useState(false);

  const isLive = sportSlug === 'live';
  const isGamesTab = activeTab === 'games';

  const togglePreviousMarket = () => setShowPreviousMarkets((prev) => !prev);

  return (
    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
      {isGamesTab && isLive && (
        <LiveView
          onTeamClick={onTeamClick}
          onPropTradeClick={onPropTradeClick}
          columnVisibility={columnVisibility}
          activeOutcomeId={activeOutcomeId}
          searchQuery={searchQuery}
        />
      )}

      {isGamesTab && !isLive && (
        <>
          {!isCurrentSportPropsLoading && games.length === 0 ? (
            /* No active games — show previous markets automatically */
            <PreviousMarketsView
              onTeamClick={onTeamClick}
              onPropTradeClick={onPropTradeClick}
              columnVisibility={columnVisibility}
              activeOutcomeId={activeOutcomeId}
              series_id={`${seriesId}`}
              searchQuery={searchQuery}
            />
          ) : (
            <>
              <GamesView
                games={games}
                gamesByDate={gamesByDate}
                sportSlug={sportSlug}
                onTeamClick={onTeamClick}
                onPropTradeClick={onPropTradeClick}
                isLoading={isCurrentSportPropsLoading}
                columnVisibility={columnVisibility}
                activeOutcomeId={activeOutcomeId}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
              />

              <PreviousMarketsToggle show={showPreviousMarkets} onToggle={togglePreviousMarket} />

              {showPreviousMarkets && (
                <PreviousMarketsView
                  onTeamClick={onTeamClick}
                  onPropTradeClick={onPropTradeClick}
                  columnVisibility={columnVisibility}
                  activeOutcomeId={activeOutcomeId}
                  series_id={`${seriesId}`}
                  searchQuery={searchQuery}
                />
              )}
            </>
          )}
        </>
      )}

      {!isGamesTab && (
        <SportPropsView
          props={propsData}
          isLoading={isPropsLoading}
          onPropTradeClick={onPropTradeClick}
        />
      )}

      {(seriesId || isLive) && showComments && (
        <Box sx={{ mt: 4 }}>
          <SportsCommentsSection seriesId={seriesId || sportSlug} />
        </Box>
      )}
    </Box>
  );
};
